import React, { useState, useEffect } from 'react';
import Sidebar from './Sidebar';
import Roadmap from './Roadmap';
import DocumentUpload from './DocumentUpload';
import { useNavigate } from 'react-router-dom';
import { GraduationCap, BookOpen, FileCheck, Plane, Home, Bell, MessageSquare, AlertCircle } from 'lucide-react';
import AccommodationManager from './AccommodationManager';
import DashboardHub from './DashboardHub';
import AdmissionsManager from './AdmissionsManager';
import AdmissionsHome from './AdmissionsHome';

import EnglishPrepLab from './services/EnglishPrepLab';
import VisaComplianceHub from './services/VisaComplianceHub';
import TravelPlanner from './services/TravelPlanner';
import CareerLaunchpad from './services/CareerLaunchpad';

const StudentDashboard: React.FC<{ initialTab?: string }> = ({ initialTab = 'dashboard' }) => {
    const navigate = useNavigate();
    const [user, setUser] = useState<any>(null);
    const [profile, setProfile] = useState<any>(null);
    const [tracking, setTracking] = useState<any>(null);
    const [documents, setDocuments] = useState<any[]>([]);
    const [tileStatuses, setTileStatuses] = useState<Record<string, string>>({});
    const [unreadMessages, setUnreadMessages] = useState(0);

    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState(initialTab);
    const [activeService, setActiveService] = useState<string | null>(null);

    const calculateCompletion = () => {
        if (!tracking) return 0;
        const totalSteps = 8;
        const step = tracking.CurrentStep || 1;
        return Math.min(Math.round(((step - 1) / totalSteps) * 100) + 10, 100);
    };

    const completion = calculateCompletion();

    const fetchData = async () => {
        try {
            const token = localStorage.getItem('token');
            if (!token) {
                navigate('/login');
                return;
            }

            const profileRes = await fetch('/api/student/profile', {
                headers: { 'Authorization': `Bearer ${token}` }
            });

            const summaryRes = await fetch('/api/student/dashboard/summary', {
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (profileRes.status === 401 || summaryRes.status === 401) {
                navigate('/login');
                return;
            }

            const profileData = await profileRes.json();
            const summaryData = await summaryRes.json();

            setUser(profileData.user);
            setProfile(profileData.profile);
            setTracking(profileData.tracking);
            setDocuments(profileData.documents || []);

            setTileStatuses(summaryData.tiles || {});
            setUnreadMessages(summaryData.unreadCount || 0);

        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    useEffect(() => {
        if (!loading && user && (!user.Country || user.Country === "")) {
            navigate('/student/setup');
        }
    }, [loading, user, navigate]);

    if (loading) return (
        <div className="flex items-center justify-center min-h-screen font-display font-bold tracking-wide"
            style={{ background: '#f7f9fb', color: '#009B8F' }}>
            Loading Your Dashboard...
        </div>
    );

    const renderDashboardContent = () => {
        if (activeService === 'admissions') return (
            <AdmissionsHome
                documents={documents}
                onUpdate={fetchData}
                onBack={() => setActiveService(null)}
                user={user}
                profile={profile}
                tracking={tracking}
            />
        );
        if (activeService === 'accommodation') return <AccommodationManager onBack={() => setActiveService(null)} />;
        if (activeService === 'visa_lab') return <VisaComplianceHub onBack={() => setActiveService(null)} />;
        if (activeService === 'prep_lab') return <EnglishPrepLab onBack={() => setActiveService(null)} />;
        if (activeService === 'travel') return <TravelPlanner onBack={() => setActiveService(null)} />;
        if (activeService === 'career_launchpad') return <CareerLaunchpad onBack={() => setActiveService(null)} />;

        return <DashboardHub tileStatuses={tileStatuses} onTileClick={setActiveService} />;
    };

    const firstName = user?.FullName ? user.FullName.split(' ')[0] : 'Student';

    return (
        <div className="flex min-h-screen" style={{ background: '#f7f9fb' }}>
            {/* Sidebar */}
            <div className="hidden md:block w-64 fixed h-full z-10">
                <Sidebar user={user} profile={profile} activeTab={activeTab} setActiveTab={setActiveTab} completion={completion} />
            </div>

            {/* Main Content */}
            <div className="flex-1 ml-0 md:ml-64 flex flex-col min-h-screen">

                <div style={{ padding: '2rem 2.5rem 3rem' }}>

                    {/* Header */}
                    <header className="rounded-2xl relative overflow-hidden mb-8"
                        style={{
                            background: 'linear-gradient(135deg, #003434 0%, #004d4d 100%)',
                            padding: '2.25rem 2.5rem',
                        }}>
                        {/* Ambient glow */}
                        <div className="absolute top-0 right-0 w-72 h-72 rounded-full blur-[120px] -mr-20 -mt-20"
                            style={{ background: 'rgba(0,229,208,0.07)' }} />

                        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 relative z-10">
                            <div>
                                <h1 className="text-2xl md:text-3xl font-display font-extrabold text-white leading-tight mb-1">
                                    Welcome back, {firstName}.
                                </h1>
                                <p className="text-sm font-ui" style={{ color: 'rgba(176,204,201,0.7)' }}>
                                    Your global academic transition is {completion}% complete.
                                </p>
                            </div>

                            {/* Status badges & bell */}
                            <div className="flex items-center gap-3">
                                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full"
                                    style={{ background: 'rgba(255,255,255,0.08)' }}>
                                    <span className="w-1.5 h-1.5 rounded-full animate-pulseOrb" style={{ background: '#00e5d0' }} />
                                    <span className="text-[10px] font-ui font-bold uppercase tracking-[0.08em] text-white">
                                        {profile?.StudentID || 'NEX-PENDING'}
                                    </span>
                                </span>

                                <button
                                    onClick={() => setActiveTab('messages')}
                                    className="relative w-9 h-9 rounded-xl flex items-center justify-center transition-all"
                                    style={{ background: 'rgba(255,255,255,0.06)' }}
                                    onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = 'rgba(255,255,255,0.12)'; }}
                                    onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = 'rgba(255,255,255,0.06)'; }}
                                    aria-label="Notifications"
                                >
                                    <Bell size={16} className="text-white" strokeWidth={1.8} />
                                    {unreadMessages > 0 && (
                                        <span className="absolute -top-1 -right-1 w-4 h-4 text-[8px] font-ui font-bold text-white rounded-full flex items-center justify-center"
                                            style={{ background: '#c62828' }}>
                                            {unreadMessages}
                                        </span>
                                    )}
                                </button>
                            </div>
                        </div>
                    </header>

                    {/* Hero Section (dashboard home only) */}
                    {activeTab === 'dashboard' && !activeService && (
                        <div className="text-center mb-12 animate-fadeIn"
                            style={{ paddingTop: '1rem', paddingBottom: '1rem' }}>
                            <h2 className="text-xl md:text-2xl font-display font-bold mb-2" style={{ color: '#191c1e' }}>
                                Helping students move beyond borders.
                            </h2>
                            <p className="text-sm font-ui max-w-lg mx-auto" style={{ color: 'rgba(25,28,30,0.5)' }}>
                                Our integrated ERP streamlines your international career and education journey with surgical precision.
                            </p>
                        </div>
                    )}

                    {/* Dashboard Tab */}
                    {activeTab === 'dashboard' && (
                        <div style={{ marginBottom: '4rem' }}>
                            {activeService && (
                                <button
                                    onClick={() => setActiveService(null)}
                                    className="flex items-center gap-2 text-xs font-ui font-bold uppercase tracking-[0.15em] transition-all mb-6"
                                    style={{ color: '#009B8F' }}
                                    onMouseEnter={e => { (e.currentTarget as HTMLElement).style.transform = 'translateX(-4px)'; }}
                                    onMouseLeave={e => { (e.currentTarget as HTMLElement).style.transform = 'translateX(0)'; }}
                                >
                                    &#8592; Return to Dashboard Hub
                                </button>
                            )}
                            {renderDashboardContent()}
                        </div>
                    )}

                    {/* Profile Tab */}
                    {activeTab === 'profile' && (
                        <div className="max-w-4xl animate-fadeIn">
                            <div className="rounded-2xl p-10" style={{ background: '#ffffff', boxShadow: '0 8px 32px rgba(25,28,30,0.06)' }}>
                                <h3 className="text-2xl font-display font-bold mb-10" style={{ color: '#191c1e' }}>
                                    Personal Profile
                                </h3>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    {[
                                        { label: 'Full Name', value: user?.FullName },
                                        { label: 'Email Address', value: user?.Email },
                                        { label: 'Phone Number', value: user?.Phone },
                                        { label: 'Target Country', value: user?.Country }
                                    ].map((item, idx) => (
                                        <div key={idx} className="p-5 rounded-xl" style={{ background: '#f2f4f6' }}>
                                            <label className="label-editorial block mb-2" style={{ color: '#6b7280', letterSpacing: '0.1em' }}>
                                                {item.label}
                                            </label>
                                            <p className="text-sm font-ui font-semibold" style={{ color: '#191c1e' }}>
                                                {item.value || 'Not Set'}
                                            </p>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Documents Tab */}
                    {activeTab === 'documents' && (
                        <div className="max-w-5xl animate-fadeIn">
                            <DocumentUpload documents={documents} onUploadSuccess={fetchData} />
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default StudentDashboard;
